import React, { Component } from 'react'
import cx from 'classnames'
import { withRouter } from 'react-router-dom'
import { injectIntl, FormattedMessage } from 'react-intl'
import NumberFormat from 'react-number-format'
import { Text, Box, Flex, Radio, RadioGroup, InputGroup, InputRightElement } from '@chakra-ui/react'
import { Form, Field } from 'react-final-form'
import Loader from 'src/components/Loader/Loader'
import {
  getBodyContainer,
  logError,
  isEmpty,
  roundDecimals,
  currencySettings,
  formatCurrency,
  getStaticUrl,
} from 'src/utils/helpers'
import { Layout, Container, Content, PageTitle } from 'src/components/Layout'
import { Card, FormPreviewCard } from 'src/components/Card'
import { CardRow, CardRowMain, CardRowSide } from 'src/components/Card/CardRow'
import { Input, TextArea } from 'src/components/Form'

import EventsService from 'src/utils/events-service'
import withToast from 'src/components/Toast'
import intlMessagesApp from 'src/modules/App/translations'
import intlMessages from '../translations'
import Button from 'src/components/Button/Button'
import FontAwesomeIconComponent from 'src/components/FontAwesomeIcon'
import Modal from 'src/components/Modal/Modal'
import styles from './styles.module.less'

import check from './check@2x.png'
import checkUK from './check@2xPounds.png'
import ThankYou from 'src/modals/ThankYou/ThankYou'
import { validateEmail } from 'src/utils/validation'

const staticUrl = getStaticUrl()

const PAYMENT_MIN_AMOUNT = 1
const PAYMENT_MAX_AMOUNT = 50000
const ROUTING_NUM_CHARACTERS = 9

class EPay extends Component {
  constructor(props) {
    super(props)

    const { user, brand } = props

    // Get currency settings and set it for this component
    const { decimalScale } = currencySettings()
    this.decimalScale = decimalScale

    // Try to find amount from selected policy
    const amount = this.getSelectedPolicyField('premium', 0)

    this.state = {
      activeScreen: 'payment', // "payment", "achHelper", "summary", "thanks"
      paymentType: '', // "card", "ach"
      amount: amount,
      amountDisplayValue: formatCurrency(amount, this.decimalScale),
      ePayPolicyAccountName: brand.hasOwnProperty('name') && brand.name ? brand.name : '',
      ePayPolicyPaymentProcessorName: 'ePayPolicy',
      ePayPolicyPaymentProcessorUrl: 'https://epaypolicy.com',
      // Card fee is received from ePayPolicy API (based on submitted amount)
      cardFeeAmount: 0,
      // Card fee percent is later re-calculated based on fee received from ePayPolicy API (based on submitted amount)
      cardFeePercent: 3.25,
      // ACH fee is received from ePayPolicy API (based on submitted amount),
      // ACH fee percent is not relevant for ACH because fee is absolute and usually $3
      achFeeAmount: 3,
      total: 0,
      cardName: '',
      cardNumber: '',
      cardNumberDisplayValue: '',
      cardMonth: '',
      cardYear: '',
      cardCvc: '',
      postalCode: '',
      notes: '',
      achBankAccountHolder: '',
      achRoutingNumber: '',
      achAccountNumber: '',
      achConfirmAccountNumber: '',
      payersName: user && user.name ? user.name : '',
      payersEmail: user && user.email ? user.email : '',
      policyNumber: this.getSelectedPolicyField('policyNumber'),
      paymentTokenId: '',
    }
  }

  componentDidMount() {
    const { isEPayPolicy, history, setCurrentlyOpenFeature } = this.props

    if (!isEPayPolicy) {
      history.push('/')
      return true
    }

    setCurrentlyOpenFeature('payments')
  }

  componentDidUpdate(prevProps) {
    const {
      intl,
      submitEPayPaymentInformationResponse,
      ePayPaymentInformationPayload,
      processEPayPaymentResponse,
      processEPayPaymentPayload,
      toast,
    } = this.props

    // If there is API response and it changed (it's empty object while submitting)
    if (
      !isEmpty(submitEPayPaymentInformationResponse) &&
      submitEPayPaymentInformationResponse !== prevProps.submitEPayPaymentInformationResponse
    ) {
      if (!submitEPayPaymentInformationResponse.success) {
        toast({
          title: intl.formatMessage(intlMessagesApp.appErrorTitle),
          description:
            submitEPayPaymentInformationResponse.message ||
            intl.formatMessage(intlMessagesApp.appErrorDescription),
          status: 'error',
        })
        return false
      }

      const cardFeeAmount =
        ePayPaymentInformationPayload && ePayPaymentInformationPayload.hasOwnProperty('cardFees')
          ? ePayPaymentInformationPayload.cardFees
          : this.state.cardFeeAmount

      const achFeeAmount =
        ePayPaymentInformationPayload && ePayPaymentInformationPayload.hasOwnProperty('achFees')
          ? ePayPaymentInformationPayload.achFees
          : this.state.achFeeAmount

      this.setState({
        activeScreen: 'summary',
        cardFeeAmount: cardFeeAmount,
        cardFeePercent: this.calculateCardFeePercentFromCardFeeAmount(
          this.state.amount,
          cardFeeAmount,
        ),
        achFeeAmount: achFeeAmount,
        total: parseFloat(this.state.amount) + this.getFeeAmount(cardFeeAmount, achFeeAmount),
      })
    }

    // If there is API response and it changed (it's empty object while submitting)
    if (
      !isEmpty(processEPayPaymentResponse) &&
      processEPayPaymentResponse !== prevProps.processEPayPaymentResponse
    ) {
      if (processEPayPaymentResponse.success === true) {
        this.setState({
          activeScreen: 'thanks',
        })

        return true
      }

      // Get ePayPolicy form errors
      const modelState =
        processEPayPaymentPayload && processEPayPaymentPayload.hasOwnProperty('modelState')
          ? processEPayPaymentPayload.modelState
          : {}

      // For some reason ePayPolicy returns some errors under modelState.postTransactionRequestModel (e.g. card year)
      const ePayPolicyError =
        !isEmpty(modelState) &&
        modelState.hasOwnProperty('postTransactionRequestModel') &&
        Array.isArray(modelState.postTransactionRequestModel)
          ? modelState.postTransactionRequestModel.join(' \n')
          : null

      const ePayPolicyErrorMessage = ePayPolicyError || processEPayPaymentResponse.message

      toast({
        title: intl.formatMessage(intlMessagesApp.appErrorTitle),
        description:
          ePayPolicyErrorMessage || intl.formatMessage(intlMessagesApp.appErrorDescription),
        status: 'error',
      })

      this.setState(
        {
          activeScreen: 'payment',
        },
        () => {
          this.processEPayPolicyFieldErrors(modelState, ePayPolicyErrorMessage)
        },
      )
    }
  }

  /**
   * Get fee amount based on current payment type.
   *
   * @param {number} cardFeeAmount
   * @param {number} achFeeAmount
   * @return {number}
   */
  getFeeAmount = (cardFeeAmount, achFeeAmount) => {
    const { paymentType } = this.state

    if (paymentType === 'card') {
      return parseFloat(cardFeeAmount)
    }

    if (paymentType === 'ach') {
      return parseFloat(achFeeAmount)
    }

    return 0
  }

  /**
   * Switch Card/Ach payment type.
   *
   * @param {Event} e Click event
   * @param {string} paymentType ["card"|"ach"] New payment type
   */
  setPaymentType = paymentType => {
    this.setState({
      paymentType: paymentType,
    })
  }

  /**
   * Check errors received from ePayPolicy and show in UI.
   *
   * But replace with our error message so it can be translated to FR.
   *
   * @param {object} fieldErrors Field errors received from ePayPolicy
   * @param {string} errorMessage Sometimes field errors are returned in general error message
   * @return {null|boolean}
   */
  processEPayPolicyFieldErrors = (fieldErrors, errorMessage = null) => {
    try {
      const { intl } = this.props

      const formatErrors = error => {
        return {
          errors: [new Error(error)],
        }
      }

      let errors = {}

      const message = errorMessage ? errorMessage.toLowerCase() : ''

      // Loosely marking errors as invalid, because ePayPolicy sometimes returns it in general error
      if (message.includes('card number') || message.includes('carte')) {
        errors.cardNumber = formatErrors(
          intl.formatMessage(intlMessages.paymentFormErrorCardNumberInvalid),
        )
      }
      if (message.includes('year') || message.includes('année')) {
        errors.cardYear = formatErrors(
          intl.formatMessage(intlMessages.paymentFormErrorCardYearInvalid),
        )
      }

      // TODO: PAT-2534 / PAT-2535 Add part of FR version
      if (message.includes('routing number')) {
        errors.achRoutingNumber = formatErrors(
          intl.formatMessage(intlMessages.paymentFormErrorAchRoutingNumberValid),
        )
      }

      // TODO: PAT-2534 / PAT-2535 Catch and replace common ACH errors, so we can have FR versions

      // ePayPolicy naming is inconsistent (e.g. upper/lower case), so we'll loosely compare it
      if (!isEmpty(fieldErrors)) {
        Object.keys(fieldErrors).map(ePayPolicyField => {
          if (isEmpty(fieldErrors[ePayPolicyField])) {
            return false
          }

          const field = ePayPolicyField.toLowerCase()

          if (field.includes('creditcardinformation.accountholder')) {
            errors.cardName = formatErrors(
              intl.formatMessage(intlMessages.paymentFormErrorNameOnTheCardInvalid),
            )
          }
          if (field.includes('creditcardinformation.cardnumber')) {
            errors.cardNumber = formatErrors(
              intl.formatMessage(intlMessages.paymentFormErrorCardNumberInvalid),
            )
          }
          if (field.includes('creditcardinformation.month')) {
            errors.cardMonth = formatErrors(
              intl.formatMessage(intlMessages.paymentFormErrorCardMonthInvalid),
            )
          }
          if (field.includes('creditcardinformation.year')) {
            errors.cardYear = formatErrors(
              intl.formatMessage(intlMessages.paymentFormErrorCardYearInvalid),
            )
          }
          if (field.includes('creditcardinformation.cvc')) {
            errors.cardCvc = formatErrors(
              intl.formatMessage(intlMessages.paymentFormErrorCardCvcInvalid),
            )
          }
          if (field.includes('creditcardinformation.postalcode')) {
            errors.postalCode = formatErrors(
              intl.formatMessage(intlMessages.paymentFormErrorCardPostalCodeInvalid),
            )
          }

          return true
        })
      }

      if (isEmpty(errors)) {
        return false
      }
      // Update form fields to show found errors
      // form.setFields(errors)
    } catch (e) {
      logError('Process ePayPolicy field errors', e)
    }
  }

  /**
   * Set error message for single input field.
   *
   * @param fieldName
   * @param errorMessage
   */
  setFieldError = (fieldName, errorMessage) => {
    this.props.form.setFields({
      [fieldName]: {
        errors: [new Error(errorMessage)],
      },
    })
  }

  /**
   * Quick and dirty form validation, since ePayPolicy doesn't return good error messages.
   *
   * And it needs to be translated to FR anyway.
   *
   *
   * // TODO: PAT-2160 Figure out why error disappear from the field and also from the preview when form is submitted
   *
   *
   * @return {boolean}
   */
  validatePaymentForm = values => {
    const { intl } = this.props

    const {
      amount,
      cardMonth,
      cardYear,
      postalCode,
      notes,
      achBankAccountHolder,
      achRoutingNumber,
      achAccountNumber,
      achConfirmAccountNumber,
      cardNumber,
      cardCvc,
      cardName,
    } = values

    const { paymentType } = this.state

    const errors = {}

    if (!(amount && amount.length > 0)) {
      errors.amount = intl.formatMessage(intlMessages.paymentFormErrorAmount)
    } else if (amount < PAYMENT_MIN_AMOUNT || amount > PAYMENT_MAX_AMOUNT) {
      console.log('max ampunt')
      errors.amount = intl.formatMessage(intlMessages.paymentFormErrorAmountInvalid, {
        min: PAYMENT_MIN_AMOUNT,
        max: PAYMENT_MAX_AMOUNT,
      })
    }

    if (paymentType === 'card') {
      if (!cardNumber) {
        errors.cardNumber = intl.formatMessage(intlMessages.paymentFormErrorCardNumber)
      }
      if (!cardCvc) {
        errors.cardCvc = intl.formatMessage(intlMessages.paymentFormErrorCardCvc)
      }
      if (!cardName) {
        errors.cardName = intl.formatMessage(intlMessages.paymentFormErrorNameOnTheCard)
      }
      if (!(cardMonth && cardMonth.length > 0)) {
        errors.cardMonth = intl.formatMessage(intlMessages.paymentFormErrorCardMonth)
      } else if (
        parseInt(cardMonth) < 1 ||
        parseInt(cardMonth) > 12 ||
        (parseInt(cardYear) === new Date().getFullYear() &&
          parseInt(cardMonth) < new Date().getMonth() + 1)
      ) {
        errors.cardMonth = intl.formatMessage(intlMessages.paymentFormErrorCardMonthInvalid)
      }
      if (!(cardYear && cardYear.length > 0)) {
        errors.cardYear = intl.formatMessage(intlMessages.paymentFormErrorCardYear)
      } else if (
        parseInt(cardYear) < new Date().getFullYear() ||
        parseInt(cardYear) > new Date().getFullYear() + 50
      ) {
        errors.cardYear = intl.formatMessage(intlMessages.paymentFormErrorCardYearInvalid)
      }
      if (postalCode && postalCode.length > 0 && postalCode.toString().length > 5) {
        errors.postalCode = intl.formatMessage(
          intlMessages.paymentFormErrorCardPostalCodeInvalidLength,
          {
            max: 5,
          },
        )
      }
    }

    if (paymentType === 'ach') {
      if (!(achBankAccountHolder && achBankAccountHolder.length > 0)) {
        errors.achBankAccountHolder = intl.formatMessage(
          intlMessages.paymentFormErrorAchBankAccountHolder,
        )
      } else if (achBankAccountHolder.toString().length > 100) {
        errors.achBankAccountHolder = intl.formatMessage(
          intlMessages.paymentFormErrorAchBankAccountHolderInvalid,
        )
      }

      if (!(achRoutingNumber && achRoutingNumber.length > 0)) {
        errors.achRoutingNumber = intl.formatMessage(intlMessages.paymentFormErrorAchRoutingNumber)
      } else if (/^[0-9]+$/.test(achRoutingNumber) === false) {
        errors.achRoutingNumber = intl.formatMessage(
          intlMessages.paymentFormErrorAchRoutingNumberNotNumber,
        )
      } else if (achRoutingNumber.toString().length !== ROUTING_NUM_CHARACTERS) {
        errors.achRoutingNumber = intl.formatMessage(
          intlMessages.paymentFormErrorAchRoutingNumberLength,
          {
            numCharacters: ROUTING_NUM_CHARACTERS,
          },
        )
      }

      if (!(achAccountNumber && achAccountNumber.length > 0)) {
        errors.achAccountNumber = intl.formatMessage(intlMessages.paymentFormErrorAchAccountNumber)
      } else if (/^[0-9]+$/.test(achAccountNumber) === false) {
        errors.achAccountNumber = intl.formatMessage(
          intlMessages.paymentFormErrorAchAccountNumberNotNumber,
        )
      } else if (achAccountNumber.toString().length > 100) {
        errors.achAccountNumber = intl.formatMessage(
          intlMessages.paymentFormErrorAchAccountNumberInvalid,
        )
      }

      if (!(achConfirmAccountNumber && achConfirmAccountNumber.length > 0)) {
        errors.achConfirmAccountNumber = intl.formatMessage(
          intlMessages.paymentFormErrorAchConfirmAccountNumber,
        )
      } else if (/^[0-9]+$/.test(achConfirmAccountNumber) === false) {
        errors.achConfirmAccountNumber = intl.formatMessage(
          intlMessages.paymentFormErrorAchConfirmAccountNumberNotNumber,
        )
      } else if (achConfirmAccountNumber !== achAccountNumber) {
        errors.achConfirmAccountNumber = intl.formatMessage(
          intlMessages.paymentFormErrorAchConfirmAccountNumberMismatch,
        )
      }
    }

    if (notes && notes.length > 0 && notes.toString().length > 500) {
      errors.notes = intl.formatMessage(intlMessages.paymentFormErrorCardNoteInvalid, {
        max: 500,
      })
    }
    return errors
  }

  /**
   * Quick and dirty form validation, since ePayPolicy doesn't return good error messages.
   *
   * And it needs to be translated to FR anyway.
   *
   * @return {boolean}
   */
  validateSummaryDetails = values => {
    const { intl } = this.props

    const { payersName, payersEmail, policyNumber } = values

    const maxLength = 150

    const errors = {}

    if (!(payersName && payersName.length > 0)) {
      errors.payersName = intl.formatMessage(intlMessages.paymentFormErrorPayersName)
    } else if (payersName.length > maxLength) {
      errors.payersName = intl.formatMessage(intlMessages.paymentFormErrorPayersNameInvalid, {
        max: maxLength,
      })
    }

    if (!payersEmail) {
      errors.payersEmail = intl.formatMessage(intlMessages.paymentFormErrorPayersEmail)
    } else if (payersEmail && !validateEmail(payersEmail)) {
      errors.payersEmail = intl.formatMessage(intlMessagesApp.appGlobalErrorEmailNotValid)
    } else if (payersEmail.length > maxLength) {
      errors.payersEmail = intl.formatMessage(intlMessages.paymentFormErrorPayersEmailInvalid, {
        max: maxLength,
      })
    }

    if (policyNumber && policyNumber.length > 0 && policyNumber.length > maxLength) {
      errors.policyNumber = intl.formatMessage(intlMessages.paymentFormErrorPolicyNumberInvalid, {
        max: maxLength,
      })
    }
    return errors
  }

  /**
   * Extract field from selected policy.
   *
   * @param {string} field Name of the field to extract
   * @param {*} defaultResult What to return if field value is not found
   * @returns {*}
   */
  getSelectedPolicyField = (field, defaultResult = null) => {
    try {
      if (!field) {
        return defaultResult
      }
      const { paymentPolicies, selectedPaymentPolicies } = this.props

      if (
        !(selectedPaymentPolicies && selectedPaymentPolicies.length > 0) &&
        selectedPaymentPolicies[0]
      ) {
        return defaultResult
      }

      if (!(paymentPolicies && paymentPolicies.length > 0)) {
        return defaultResult
      }

      const policy = paymentPolicies.find(
        p => p.hasOwnProperty('id') && p.id === selectedPaymentPolicies[0],
      )

      if (!(policy && policy.hasOwnProperty(field) && policy[field] && policy[field].length > 0)) {
        return defaultResult
      }

      return policy[field]
    } catch (e) {
      logError('Get selected policy number', e)
      return defaultResult
    }
  }

  /**
   * Handle clicking "Next" button on Payment screen.
   *
   * @param {Event} event Click event
   */
  submitPaymentDetails = values => {
    try {
      const { intl, submitEPayPaymentInformation, toast } = this.props

      const { amount } = values

      const { paymentType } = this.state

      if (isEmpty(paymentType)) {
        toast({
          title: intl.formatMessage(intlMessagesApp.appErrorTitle),
          description: intl.formatMessage(intlMessages.paymentErrorPaymentType),
          status: 'warning',
        })
        return false
      }
      // Currently just to get payment fees
      submitEPayPaymentInformation({
        amount: amount,
      })

      this.setState({
        amount: values.amount,
        cardCvc: values.cardCvc,
        cardMonth: values.cardMonth,
        cardName: values.cardName,
        cardNumber: values.cardNumber ? values.cardNumber.split(' ').join('') : values.cardNumber,
        cardYear: values.cardYear,
        notes: values.notes,
        postalCode: values.postalCode,
        achBankAccountHolder: values.achBankAccountHolder,
        achRoutingNumber: values.achRoutingNumber,
        achAccountNumber: values.achAccountNumber,
      })
    } catch (e) {
      logError('Submit payment details', e)
    }
  }

  /**
   * Handle clicking "Send" button on Summary screen.
   *
   * @param {Event} event Click event
   */
  processEPayPayment = async values => {
    try {
      const { processEPayPayment, isWidgetState } = this.props

      const { payersName, payersEmail, policyNumber } = values

      const {
        paymentType,
        amount,
        cardName,
        cardNumber,
        cardMonth,
        cardYear,
        cardCvc,
        postalCode,
        notes,
        cardFeeAmount,
        achFeeAmount,
        achBankAccountHolder,
        achRoutingNumber,
        achAccountNumber,
      } = this.state

      // Show basic errors, but ePayPolicy validation is more important

      let attributeValues = {}

      if (policyNumber && policyNumber.length > 0) {
        attributeValues.policyNumber = policyNumber
      }
      if (notes && notes.length > 0) {
        attributeValues.notes = notes
      }

      let paymentData = {
        paymentType: paymentType,
        cardFees: cardFeeAmount,
        achFees: achFeeAmount,
        amount: amount,
        payer: payersName,
        emailAddress: payersEmail,
        attributeValues: attributeValues,
      }

      if (paymentType === 'card') {
        paymentData.creditCardInformation = {
          accountHolder: cardName,
          cardNumber: cardNumber,
          month: cardMonth,
          year: cardYear,
          cvc: cardCvc,
          postalCode: postalCode || '',
        }
      }

      if (paymentType === 'ach') {
        paymentData.bankAccountInformation = {
          accountHolder: achBankAccountHolder,
          routingNumber: achRoutingNumber,
          accountNumber: achAccountNumber,
        }
      }
      // Submit payment information to get payment token, fees, etc.
      processEPayPayment({
        paymentData: paymentData,
        visitorData: await EventsService.getVisitorData({ isWidget: isWidgetState }),
      })
    } catch (e) {
      logError('Submit payment details', e)
    }
  }

  /**
   * Used to find fee percentage from fee amount (which we got from ePayPolicy API).
   * Fee percent is currently used just for display purposes.
   *
   * @param {number} amount
   * @param {number} cardFeeAmount
   * @return {*}
   * @constructor
   */
  calculateCardFeePercentFromCardFeeAmount = (amount, cardFeeAmount) => {
    if (!(amount > 0 && cardFeeAmount > 0)) {
      return 0
    }

    return roundDecimals((cardFeeAmount / amount) * 100, 2)
  }

  /**
   * This is just to have real-time calculations,
   * but actual fee amount is received when amount is submitted to ePayPolicy API,
   * and then we calculate fee percent from that (just for display purposes).
   *
   * @param {number} amount
   * @param {number} cardFeePercent
   * @return {*}
   * @constructor
   */
  calculateCardFeeAmountFromCardFeePercent = (amount, cardFeePercent) => {
    if (!(cardFeePercent > 0 && amount > 0)) {
      return 0
    }

    return (amount * cardFeePercent) / 100
  }

  /**
   * Update state when form fields are changed.
   *
   * @param {Event} event Change event
   * @param {string} fieldState Change event
   * @return {void}
   */
  handleFieldChange = (event, fieldState) => {
    this.setState({
      [fieldState]: event.target.value,
    })
  }

  /**
   * When field is edited in preview popup, close it when enter is clicked.
   *
   * @param event
   * @param setIsEdited Function for closing field popup
   */
  handleFieldPreviewKeyPress = (event, setIsEdited) => {
    if (event.key === 'Enter') {
      // Do not submit the form
      event.preventDefault()

      setIsEdited(false)
    }
  }

  /**
   * When amount field value is changed update state and calculate fees and total.
   *
   * @param {object} amount Object containing formatted and not formatted values
   * @return {void}
   */
  updateAmountValue = amount => {
    // Get not formatted value
    const { value } = amount

    const { achFeeAmount } = this.props

    // Calculate new fee amount based on the current fee percent
    const cardFeeAmount = this.calculateCardFeeAmountFromCardFeePercent(
      value,
      this.state.cardFeePercent,
    )

    this.setState({
      amount: value,
      amountDisplayValue: value > 0 ? formatCurrency(value, this.decimalScale) : '',
      total: parseFloat(value) + this.getFeeAmount(cardFeeAmount, achFeeAmount),
      cardFeeAmount: cardFeeAmount,
    })
  }

  /**
   * Show ACH helper popup with instructions for finding routing and account number.
   */
  showAchHelper = () => {
    this.setState({
      activeScreen: 'achHelper',
    })
  }

  /**
   * Close ACH helper popup.
   */
  hideAchHelper = () => {
    this.setState({
      activeScreen: 'payment',
    })
  }

  /**
   * When card number field value is changed update state with not formatted value.
   *
   * @param {object} values Object containing formatted and not formatted values
   * @return {void}
   */
  handleCardNumberChange = values => {
    // Get not formatted value
    const { value, formattedValue } = values

    this.setState({
      cardNumber: value.split(' ').join(''),
      cardNumberDisplayValue: formattedValue,
    })
  }

  /**
   * Handle clicking back arrow in header.
   *
   * @return {void|boolean}
   */
  handleBack = () => {
    const { activeScreen } = this.state
    const { history, user } = this.props

    if (activeScreen === 'achHelper') {
      this.hideAchHelper()
      return true
    }

    if (activeScreen === 'summary') {
      this.setState({
        activeScreen: 'payment',
      })
      return true
    }

    if (!isEmpty(user)) {
      history.push('/payments/u')
      return true
    }

    history.push('/')
  }

  /**
   * Redirect back to home page.
   *
   * @param {Event} event Click event
   * @return {void}
   */
  handleFinish = () => {
    const { history } = this.props

    if (history) {
      // Go back to home page
      history.push('/')
    }
  }

  validateFormFields = values => {
    const { activeScreen } = this.state

    const isPayment = activeScreen === 'payment'
    const isSummary = activeScreen === 'summary'

    if (isSummary) {
      return this.validateSummaryDetails(values)
    } else if (isPayment) {
      return this.validatePaymentForm(values)
    }
  }

  handleBackSummary = () => {
    this.setState({
      activeScreen: 'payment',
    })
  }

  renderThankYouContent = () => {
    const { intl } = this.props
    const { total } = this.state
    return (
      <>
        <Text fontSize='16px' align='center'>
          {intl.formatMessage(intlMessages.paymentLabelTotalPaymentAmount)}
        </Text>
        <Text fontSize='16px' align='center' fontWeight='bold'>
          {formatCurrency(total, this.decimalScale)}
        </Text>
      </>
    )
  }

  amountFormatter = value => {
    return formatCurrency(value, this.decimalScale)
  }

  render() {
    const {
      activeScreen,
      paymentType,
      amount,
      cardFeePercent,
      cardFeeAmount,
      achFeeAmount,
      total,
      cardName,
      cardNumber,
      cardNumberDisplayValue,
      cardYear,
      cardMonth,
      cardCvc,
      postalCode,
      notes,
      achBankAccountHolder,
      achRoutingNumber,
      achAccountNumber,
      achConfirmAccountNumber,
      payersName,
      payersEmail,
      policyNumber,
      ePayPolicyAccountName,
      ePayPolicyPaymentProcessorName,
      ePayPolicyPaymentProcessorUrl,
    } = this.state

    const {
      intl,
      isEPayPaymentInformationInProgress,
      isProcessEPayInProgress,
      user,
      features,
      language,
      countryIsoCode,
    } = this.props

    const isEmptyPaymentType = isEmpty(paymentType)

    const feeAmount = this.getFeeAmount(cardFeeAmount, achFeeAmount)

    const isPayment = activeScreen === 'payment'
    const isAchHelper = activeScreen === 'achHelper'
    const isSummary = activeScreen === 'summary'
    const isThanks = activeScreen === 'thanks'

    const paymentProgressNumberClassNames = cx({
      [styles.progressNumber]: true,
      'kiosk-color-primary--background': isPayment || isSummary,
      'kiosk-color-primary--border-color': isPayment || isSummary,
      'kiosk-color-primary-contrast-color': isPayment,
    })
    const paymentProgressTitleClassNames = cx({
      [styles.progressTitle]: true,
      [styles.highlightProgressTitle]: isPayment || isSummary,
    })

    const summaryProgressNumberClassNames = cx({
      [styles.progressNumber]: true,
      'kiosk-color-primary--background': isSummary,
      'kiosk-color-primary--border-color': isSummary,
      'kiosk-color-primary-contrast-color': isSummary,
    })
    const paymentTypeCardCardClassName = cx({
      [styles.paymentTypeCard]: true,
      [styles.activePaymentTypeCard]: paymentType === 'card',
    })
    const paymentTypeCardAchClassName = cx({
      [styles.paymentTypeCard]: true,
      [styles.activePaymentTypeCard]: paymentType === 'ach',
    })

    const paymentTypeToggle = cx({
      [styles.paymentTypeToggle]: true,
      [styles.paymentTypeToggleForFrench]: language === 'fr',
    })

    const usePlaneFields = isEmpty(user)

    const foundedFeature = features.find(f => f.id == 'payments')
    const title =
      foundedFeature && foundedFeature.title
        ? foundedFeature.title
        : intl.formatMessage(intlMessages.paymentHeadline)

    const getCheckImage = () => {
      const isUk = countryIsoCode && countryIsoCode.toLowerCase() === 'gb'
      return isUk ? checkUK : check
    }

    return (
      <Layout>
        <Content>
          <PageTitle title={'ePay Policy'} subtitle={title} goBack={this.handleBack} />
          <Container padder className={'global-centered-desktop-content'}>
            {!isThanks && !isAchHelper ? (
              <div className={styles.progressHolder}>
                <div className={styles.progress}>
                  <Box
                    className={cx({
                      [styles.progressItem]: true,
                      [styles.activeProgressItem]: isPayment,
                    })}
                  >
                    <span className={paymentProgressNumberClassNames}>
                      {isPayment ? '1' : <span className={styles.progressCheck} />}
                    </span>
                    <Text
                      fontSize={'14px'}
                      fontWeight={500}
                      color='text.primary'
                      className={paymentProgressTitleClassNames}
                    >
                      {intl.formatMessage(intlMessages.paymentLabelEPayPayment)}
                    </Text>
                  </Box>
                  <Box
                    className={cx({
                      [styles.progressItem]: true,
                      [styles.activeProgressItem]: isSummary,
                    })}
                    opacity={isSummary ? '1' : 0.5}
                  >
                    <span className={summaryProgressNumberClassNames}>2</span>
                    <Text fontSize={'14px'} fontWeight={500} color='text-primary'>
                      {intl.formatMessage(intlMessages.paymentLabelEPaySummary)}
                    </Text>
                  </Box>
                </div>
              </div>
            ) : (
              ''
            )}
            {isEPayPaymentInformationInProgress || isProcessEPayInProgress ? (
              <Loader styleVariation='staticWithHeight' />
            ) : (
              <Box pb={'20px'}>
                <Form
                  validate={this.validateFormFields}
                  onSubmit={isSummary ? this.processEPayPayment : this.submitPaymentDetails}
                  render={({ handleSubmit }) => (
                    <form onSubmit={handleSubmit}>
                      {isPayment ? (
                        <>
                          <Field
                            name='amount'
                            initialValue={amount}
                            render={({ input, meta }) => (
                              <Box mb={2}>
                                <Input
                                  placeholder={intl.formatMessage(intlMessages.paymentLabelAmount)}
                                  error={meta}
                                  formatter={this.amountFormatter}
                                  name='amount'
                                  {...input}
                                />
                              </Box>
                            )}
                          />

                          <Text
                            fontWeight={500}
                            fontSize={'18px'}
                            variant='secondary'
                            align={'center'}
                            my={'20px'}
                          >
                            {intl.formatMessage(intlMessages.paymentTitlePaymentType)}
                          </Text>

                          <Field
                            type='radio'
                            name='paymentType'
                            initialValue={this.state.paymentType}
                            render={() => (
                              <Box mb={2}>
                                <RadioGroup
                                  defaultValue={this.state.paymentType}
                                  value={this.state.paymentType}
                                  variant='accent'
                                  name='paymentType'
                                  onChange={e => this.setPaymentType(e)}
                                  className={paymentTypeToggle}
                                >
                                  <Box
                                    className={paymentTypeCardCardClassName}
                                    cursor={'pointer'}
                                    onClick={() => this.setPaymentType('card')}
                                  >
                                    <Box>
                                      <FontAwesomeIconComponent
                                        icon={['far', 'credit-card']}
                                        size='sm'
                                        type='card-font'
                                      />
                                    </Box>

                                    <Text
                                      fontWeight={500}
                                      mt={'10px'}
                                      fontSize={'18px'}
                                      variant='card'
                                      align={'center'}
                                    >
                                      {intl.formatMessage(intlMessages.paymentTitlePaymentTypeCard)}
                                    </Text>
                                    <Radio
                                      variant='accent'
                                      mt={'10px'}
                                      for='paymentType'
                                      value='card'
                                    />
                                  </Box>
                                  <Box
                                    className={paymentTypeCardAchClassName}
                                    cursor={'pointer'}
                                    onClick={() => this.setPaymentType('ach')}
                                  >
                                    <Box>
                                      <FontAwesomeIconComponent
                                        icon={['far', 'bank']}
                                        size='sm'
                                        type='card-font'
                                      />
                                    </Box>
                                    <Text
                                      mt={'10px'}
                                      fontSize={'18px'}
                                      variant='card'
                                      textTransform='uppercase'
                                      align={'center'}
                                    >
                                      {intl.formatMessage(intlMessages.paymentTitlePaymentTypeAch)}
                                    </Text>
                                    <Radio
                                      variant='accent'
                                      mt={'10px'}
                                      for='paymentType'
                                      value='ach'
                                    />
                                  </Box>
                                </RadioGroup>
                              </Box>
                            )}
                          />

                          {!isEmptyPaymentType ? (
                            <Text
                              fontWeight={500}
                              fontSize={'18px'}
                              variant='secondary'
                              align={'center'}
                              my={'20px'}
                            >
                              {intl.formatMessage(intlMessages.paymentLabelPaymentInformation)}
                            </Text>
                          ) : (
                            ''
                          )}

                          {paymentType ? (
                            <div className={styles.formContainer}>
                              {paymentType === 'card' ? (
                                <div>
                                  <div>
                                    <label htmlFor='cardName' className={styles.fieldLabel}>
                                      {intl.formatMessage(intlMessages.paymentLabelNameOnTheCard)}
                                    </label>

                                    <Field
                                      name='cardName'
                                      initialValue={cardName}
                                      render={({ input, meta }) => (
                                        <Box mb={2}>
                                          <Input
                                            placeholder={intl.formatMessage(
                                              intlMessages.paymentPlaceholderNameOnTheCard,
                                            )}
                                            error={meta}
                                            name='cardName'
                                            {...input}
                                          />
                                        </Box>
                                      )}
                                    />
                                  </div>
                                  <div>
                                    <label htmlFor='cardNumber' className={styles.fieldLabel}>
                                      {intl.formatMessage(intlMessages.paymentLabelCardNumber)}
                                    </label>
                                    <Field
                                      name='cardNumber'
                                      initialValue={cardNumberDisplayValue}
                                      render={({ input, meta }) => (
                                        <InputGroup mb={2}>
                                          <NumberFormat
                                            name='cardNumber'
                                            format='#### #### #### ####'
                                            mask='x'
                                            onValueChange={this.handleCardNumberChange}
                                            error={meta}
                                            placeholder={'xxxx xxxx xxxx xxxx'}
                                            customInput={Input}
                                            {...input}
                                          />
                                          <InputRightElement h='full' maxH={'44px'}>
                                            <FontAwesomeIconComponent
                                              icon={['far', 'credit-card']}
                                              size='xs'
                                              type={'card-font'}
                                            />
                                          </InputRightElement>
                                        </InputGroup>
                                      )}
                                    />
                                  </div>

                                  <div>
                                    <label htmlFor='cardMonth' className={styles.fieldLabel}>
                                      {intl.formatMessage(intlMessages.paymentLabelExpirationDate)}
                                    </label>
                                    <div className={styles.cardMonthYearCvc}>
                                      <div className={styles.cardMonth}>
                                        <Field
                                          name='cardMonth'
                                          initialValue={cardMonth}
                                          render={({ input, meta }) => (
                                            <Box mb={2}>
                                              <NumberFormat
                                                name='cardMonth'
                                                format='##'
                                                mask={['M', 'M']}
                                                placeholder='MM'
                                                error={meta}
                                                customInput={Input}
                                                {...input}
                                              />
                                            </Box>
                                          )}
                                        />
                                      </div>
                                      <div className={styles.cardMonthYearSeparator}>/</div>
                                      <div className={styles.cardYear}>
                                        <Field
                                          name='cardYear'
                                          initialValue={cardYear}
                                          render={({ input, meta }) => (
                                            <Box mb={2}>
                                              <NumberFormat
                                                name='cardYear'
                                                format='####'
                                                error={meta}
                                                mask={['Y', 'Y', 'Y', 'Y']}
                                                placeholder='YYYY'
                                                customInput={Input}
                                                {...input}
                                              />
                                            </Box>
                                          )}
                                        />
                                      </div>
                                    </div>
                                  </div>

                                  <div>
                                    <label htmlFor='postalCode' className={styles.fieldLabel}>
                                      CVC /{' '}
                                      {intl.formatMessage(intlMessages.paymentLabelCardPostalCode)}
                                    </label>
                                    <div className={styles.cardPostalCodeHolder}>
                                      <div className={styles.cardCvc}>
                                        <Field
                                          name='cardCvc'
                                          initialValue={cardCvc}
                                          render={({ input, meta }) => (
                                            <Box mb={2}>
                                              <NumberFormat
                                                name='cardCvc'
                                                format='###'
                                                placeholder='CVC'
                                                error={meta}
                                                customInput={Input}
                                                {...input}
                                              />
                                            </Box>
                                          )}
                                        />
                                      </div>
                                      <div className={styles.cardPostalCode}>
                                        <Field
                                          name='postalCode'
                                          initialValue={postalCode}
                                          render={({ input, meta }) => (
                                            <Box mb={2}>
                                              <Input
                                                name='postalCode'
                                                type='text'
                                                error={meta}
                                                placeholder={intl.formatMessage(
                                                  intlMessages.paymentLabelCardPostalCode,
                                                )}
                                                {...input}
                                              />
                                            </Box>
                                          )}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                ''
                              )}

                              {paymentType === 'ach' ? (
                                <div>
                                  <div>
                                    <label
                                      htmlFor='achBankAccountHolder'
                                      className={styles.fieldLabel}
                                    >
                                      {intl.formatMessage(
                                        intlMessages.paymentLabelAchBankAccountHolder,
                                      )}
                                    </label>
                                    <Field
                                      name='achBankAccountHolder'
                                      initialValue={achBankAccountHolder}
                                      render={({ input, meta }) => (
                                        <Box mb={2}>
                                          <Input
                                            name='achBankAccountHolder'
                                            type='text'
                                            error={meta}
                                            placeholder={intl.formatMessage(
                                              intlMessages.paymentLabelAchBankAccountHolder,
                                            )}
                                            {...input}
                                          />
                                        </Box>
                                      )}
                                    />
                                  </div>
                                  <div>
                                    <label
                                      htmlFor='achRoutingNumberTxt'
                                      className={styles.fieldLabel}
                                    >
                                      {intl.formatMessage(
                                        intlMessages.paymentLabelAchRoutingNumber,
                                      )}
                                    </label>
                                    <div className={styles.helpIconParent}>
                                      <Field
                                        name='achRoutingNumber'
                                        initialValue={achRoutingNumber}
                                        render={({ input, meta }) => (
                                          <Box mb={2}>
                                            <NumberFormat
                                              name='achRoutingNumber'
                                              allowNegative={false}
                                              allowLeadingZeros={true}
                                              placeholder={intl.formatMessage(
                                                intlMessages.paymentLabelAchRoutingNumber,
                                              )}
                                              customInput={Input}
                                              error={meta}
                                              {...input}
                                            />
                                          </Box>
                                        )}
                                      />

                                      <Box
                                        className={styles.helpIconHolder}
                                        onClick={this.showAchHelper}
                                      >
                                        <FontAwesomeIconComponent
                                          icon={['far', 'circle-question']}
                                          size='xs'
                                          type='input-icon-color'
                                        />
                                      </Box>
                                    </div>
                                  </div>
                                  <div>
                                    <label htmlFor='achAccountNumber' className={styles.fieldLabel}>
                                      {intl.formatMessage(
                                        intlMessages.paymentLabelAchAccountNumber,
                                      )}
                                    </label>
                                    <div className={styles.helpIconParent}>
                                      <Field
                                        name='achAccountNumber'
                                        initialValue={achAccountNumber}
                                        render={({ input, meta }) => (
                                          <Box mb={2}>
                                            <NumberFormat
                                              name='achAccountNumber'
                                              allowNegative={false}
                                              error={meta}
                                              allowLeadingZeros={true}
                                              placeholder={intl.formatMessage(
                                                intlMessages.paymentLabelAchAccountNumber,
                                              )}
                                              customInput={Input}
                                              {...input}
                                            />
                                          </Box>
                                        )}
                                      />
                                      <Box
                                        className={styles.helpIconHolder}
                                        onClick={this.showAchHelper}
                                      >
                                        <FontAwesomeIconComponent
                                          icon={['far', 'circle-question']}
                                          size='xs'
                                          type='input-icon-color'
                                        />
                                      </Box>
                                    </div>
                                  </div>
                                  <div>
                                    <label
                                      htmlFor='achConfirmAccountNumber'
                                      className={styles.fieldLabel}
                                    >
                                      {intl.formatMessage(
                                        intlMessages.paymentLabelAchConfirmAccountNumber,
                                      )}
                                    </label>
                                    <Field
                                      name='achConfirmAccountNumber'
                                      initialValue={achConfirmAccountNumber}
                                      render={({ input, meta }) => (
                                        <Box mb={2}>
                                          <NumberFormat
                                            name='achConfirmAccountNumber'
                                            allowNegative={false}
                                            allowLeadingZeros={true}
                                            error={meta}
                                            placeholder={intl.formatMessage(
                                              intlMessages.paymentLabelAchConfirmAccountNumber,
                                            )}
                                            customInput={Input}
                                            {...input}
                                          />
                                        </Box>
                                      )}
                                    />
                                  </div>
                                </div>
                              ) : (
                                ''
                              )}

                              {!isEmptyPaymentType ? (
                                <div>
                                  <label
                                    htmlFor='notes'
                                    className={
                                      styles.fieldLabel + ' ' + styles.fieldLabelNoTopSpace
                                    }
                                  >
                                    {intl.formatMessage(intlMessages.paymentLabelPaymentNotes)}
                                  </label>
                                  <Field
                                    name='notes'
                                    initialValue={notes}
                                    render={({ input, meta }) => (
                                      <Box mt={'16px'}>
                                        <TextArea
                                          borderRadius='lg'
                                          name='notes'
                                          error={meta}
                                          resize='none'
                                          placeholder={intl.formatMessage(
                                            intlMessages.paymentLabelPaymentNotesPlaceholder,
                                          )}
                                          {...input}
                                        />
                                      </Box>
                                    )}
                                  />
                                </div>
                              ) : (
                                ''
                              )}
                            </div>
                          ) : null}

                          <Button isFullWidth size='lg' type='submit'>
                            {intl.formatMessage(intlMessagesApp.appGlobalNext)}
                          </Button>
                        </>
                      ) : (
                        ''
                      )}

                      <Modal
                        isOpen={isAchHelper}
                        isCentered
                        closable={false}
                        footer={null}
                        onClose={this.hideAchHelper}
                        renderContent={() => {
                          return (
                            <>
                              <div className={styles.achHelperContent}>
                                <h2 className={styles.achHelperTitle}>
                                  {intl.formatMessage(
                                    intlMessages.paymentLabelAchAccountNumberTitle,
                                  )}
                                </h2>
                                <div className={styles.achHelperCheckHolder}>
                                  <img
                                    src={staticUrl + getCheckImage()}
                                    alt='Check'
                                    className={styles.achCheckImage}
                                  />
                                </div>
                              </div>
                              <Button isFullWidth onClick={this.hideAchHelper}>
                                {intl.formatMessage(intlMessagesApp.appGlobalClose)}
                              </Button>
                            </>
                          )
                        }}
                        getContainer={() => getBodyContainer()}
                      ></Modal>

                      {isSummary ? (
                        <div>
                          <Text
                            fontWeight={500}
                            fontSize={'18px'}
                            variant='secondary'
                            align={'center'}
                            my={'20px'}
                          >
                            {intl.formatMessage(intlMessages.paymentLabelEPaySummary)}
                          </Text>

                          <div className={styles.formContainer}>
                            <FormPreviewCard
                              className={styles.summaryFormPreviewCard}
                              isPlain={usePlaneFields}
                            >
                              <label htmlFor='payersName' className={styles.fieldLabel}>
                                {intl.formatMessage(intlMessages.paymentLabelPayersName)}
                              </label>
                              <Field
                                name='payersName'
                                initialValue={payersName}
                                render={({ input, meta }) => (
                                  <Box mt={'16px'} mb={'25px'}>
                                    <Input
                                      name='payersName'
                                      type='text'
                                      placeholder={intl.formatMessage(
                                        intlMessages.paymentLabelPayersName,
                                      )}
                                      error={meta}
                                      {...input}
                                    />
                                  </Box>
                                )}
                              />

                              <label htmlFor='payersEmail' className={styles.fieldLabel}>
                                {intl.formatMessage(intlMessages.paymentLabelPayersEmail)}
                              </label>
                              <Field
                                name='payersEmail'
                                initialValue={payersEmail}
                                render={({ input, meta }) => (
                                  <Box mt={'16px'} mb={'25px'}>
                                    <Input
                                      name='payersEmail'
                                      type='text'
                                      placeholder={intl.formatMessage(
                                        intlMessages.paymentLabelPayersEmail,
                                      )}
                                      error={meta}
                                      {...input}
                                    />
                                  </Box>
                                )}
                              />

                              <label htmlFor='policyNumber' className={styles.fieldLabel}>
                                {intl.formatMessage(intlMessages.paymentLabelPolicyNumber)}
                              </label>
                              <Field
                                name='policyNumber'
                                initialValue={policyNumber}
                                render={({ input, meta }) => (
                                  <Box mt={'16px'} mb={'25px'}>
                                    <Input
                                      name='policyNumber'
                                      type='text'
                                      error={meta}
                                      placeholder={intl.formatMessage(
                                        intlMessages.paymentLabelPolicyNumber,
                                      )}
                                      {...input}
                                    />
                                  </Box>
                                )}
                              />
                            </FormPreviewCard>
                          </div>

                          <div>
                            <Card className={styles.previewCard}>
                              <div className={styles.previewCardDetails}>
                                {paymentType === 'card' && cardNumber ? (
                                  <div className={styles.previewCardDetail}>
                                    <Text lineHeight={'18px'} variant='card' fontSize={'14px'}>
                                      {intl.formatMessage(intlMessages.paymentLabelCard)}
                                    </Text>
                                    <Text
                                      lineHeight={'21px'}
                                      variant='card'
                                      fontSize={'16px'}
                                      opacity={0.5}
                                      className={styles.previewCardDetailValue}
                                    >
                                      {cardNumber
                                        .replace(/\d(?=\d{4})/g, 'X')
                                        .match(/.{1,4}/g)
                                        .join(' ')}
                                    </Text>
                                  </div>
                                ) : (
                                  ''
                                )}
                                {paymentType === 'card' && postalCode ? (
                                  <div className={styles.previewCardDetail}>
                                    <Text lineHeight={'18px'} variant='card' fontSize={'14px'}>
                                      {intl.formatMessage(intlMessages.paymentLabelCardPostalCode)}
                                    </Text>
                                    <Text
                                      lineHeight={'21px'}
                                      variant='card'
                                      fontSize={'16px'}
                                      opacity={0.5}
                                      className={styles.previewCardDetailValue}
                                    >
                                      {postalCode}
                                    </Text>
                                  </div>
                                ) : (
                                  ''
                                )}
                                {paymentType === 'ach' && achBankAccountHolder ? (
                                  <div className={styles.previewCardDetail}>
                                    <Text lineHeight={'18px'} variant='card' fontSize={'14px'}>
                                      {intl.formatMessage(
                                        intlMessages.paymentLabelAchBankAccountHolder,
                                      )}
                                    </Text>
                                    <Text
                                      lineHeight={'21px'}
                                      variant='card'
                                      fontSize={'16px'}
                                      opacity={0.5}
                                      className={styles.previewCardDetailValue}
                                    >
                                      {achBankAccountHolder}
                                    </Text>
                                  </div>
                                ) : (
                                  ''
                                )}
                                {paymentType === 'ach' && achRoutingNumber ? (
                                  <div className={styles.previewCardDetail}>
                                    <Text lineHeight={'18px'} variant='card' fontSize={'14px'}>
                                      {intl.formatMessage(
                                        intlMessages.paymentLabelAchRoutingNumber,
                                      )}
                                    </Text>
                                    <Text
                                      lineHeight={'21px'}
                                      variant='card'
                                      fontSize={'16px'}
                                      opacity={0.5}
                                      className={styles.previewCardDetailValue}
                                    >
                                      {achRoutingNumber}
                                    </Text>
                                  </div>
                                ) : (
                                  ''
                                )}
                                {paymentType === 'ach' && achAccountNumber ? (
                                  <div className={styles.previewCardDetail}>
                                    <Text lineHeight={'18px'} variant='card' fontSize={'14px'}>
                                      {intl.formatMessage(
                                        intlMessages.paymentLabelAchAccountNumber,
                                      )}
                                    </Text>
                                    <Text
                                      lineHeight={'21px'}
                                      variant='card'
                                      fontSize={'16px'}
                                      opacity={0.5}
                                      className={styles.previewCardDetailValue}
                                    >
                                      {achAccountNumber}
                                    </Text>
                                  </div>
                                ) : (
                                  ''
                                )}
                                {notes ? (
                                  <div className={styles.previewCardDetail}>
                                    <Text lineHeight={'18px'} variant='card' fontSize={'14px'}>
                                      {intl.formatMessage(intlMessages.paymentLabelPaymentNotes)}
                                    </Text>
                                    <Text
                                      lineHeight={'21px'}
                                      variant='card'
                                      fontSize={'16px'}
                                      opacity={0.5}
                                      className={styles.previewCardDetailValue}
                                    >
                                      {notes}
                                    </Text>
                                  </div>
                                ) : (
                                  ''
                                )}
                              </div>

                              <div className={styles.previewAmounts}>
                                <CardRow className={styles.previewCardRow}>
                                  <CardRowMain>
                                    <Text variant='card' fontSize={'14px'}>
                                      {intl.formatMessage(intlMessages.paymentLabelAmount)}
                                    </Text>
                                  </CardRowMain>
                                  <CardRowSide>
                                    <Text variant='card' fontSize={'14px'} opacity={0.5}>
                                      {formatCurrency(amount || 0, this.decimalScale)}
                                    </Text>
                                  </CardRowSide>
                                </CardRow>
                                <CardRow className={styles.previewCardRow}>
                                  <CardRowMain>
                                    <Text variant='card' fontSize={'14px'}>
                                      {intl.formatMessage(intlMessages.paymentLabelFee)}
                                      {paymentType === 'card' ? ' (' + cardFeePercent + '%)' : ''}
                                    </Text>
                                  </CardRowMain>
                                  <CardRowSide>
                                    <Text variant='card' fontSize={'14px'} opacity={0.5}>
                                      {formatCurrency(feeAmount, this.decimalScale)}
                                    </Text>
                                  </CardRowSide>
                                </CardRow>
                                <CardRow className={styles.previewCardRow}>
                                  <CardRowMain>
                                    <Text variant='card' fontSize={'14px'}>
                                      {intl.formatMessage(intlMessages.paymentLabelTotal)}
                                    </Text>
                                  </CardRowMain>
                                  <CardRowSide className={styles.previewAmountsTotal}>
                                    <Text variant='card' fontSize={'14px'} opacity={0.5}>
                                      {formatCurrency(total, this.decimalScale)}
                                    </Text>
                                  </CardRowSide>
                                </CardRow>
                              </div>
                            </Card>

                            <div className={styles.disclaimer}>
                              <Text variant='secondary' fontSize={'14px'} align={'center'}>
                                <FormattedMessage
                                  id='app.payment.label.ePayPaymentSummaryText'
                                  values={{
                                    feeAmount: formatCurrency(feeAmount, this.decimalScale),
                                    ePayPolicyAccountName: ePayPolicyAccountName,
                                    EPayPolicyAccountLink: (
                                      <a
                                        href={ePayPolicyPaymentProcessorUrl}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={'kiosk--chakra-colors-accent'}
                                      >
                                        {ePayPolicyPaymentProcessorName}
                                      </a>
                                    ),
                                  }}
                                  defaultMessage={
                                    'By clicking “Send”, I authorize {ePayPolicyAccountName} to debit my account. I understand that this payment includes a {feeAmount} fee assessed by {EPayPolicyAccountLink}.'
                                  }
                                />
                              </Text>
                            </div>

                            <Flex gap='20px'>
                              <Button
                                variant='outline'
                                isFullWidth
                                onClick={this.handleBackSummary}
                              >
                                {intl.formatMessage(intlMessagesApp.appGlobalBack)}
                              </Button>
                              <Button type='submit' isFullWidth>
                                {intl.formatMessage(intlMessagesApp.appGlobalSend)}
                              </Button>
                            </Flex>
                          </div>
                        </div>
                      ) : (
                        ''
                      )}

                      <ThankYou
                        visible={isThanks}
                        title={intl.formatMessage(intlMessages.paymentLabelThankYouTitle)}
                        onClose={this.handleFinish}
                        renderContent={this.renderThankYouContent()}
                      />
                    </form>
                  )}
                />
              </Box>
            )}
          </Container>
        </Content>
      </Layout>
    )
  }
}

export default withRouter(withToast(injectIntl(EPay)))
